import { PaymentContext } from "src/app/models/payment-availability.model";
import { inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";

import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";

import { HCurrencyPipe } from "../../../../app/pipes";
import { PopUpDialogInformalService } from "../../../services/confirms/popUpDialogInformal.service";
import { PopUpDialogConfirmService } from "../../../services/confirms/popUpDialogConfirmService.service";
import { StateService } from "../../../services/state.service";
import { DropdownOption } from "../../../components/ui-components/dropdown/dropdown.model";
import { PopUpInformalType } from "../../../components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model";
import { FormInputType } from "../../../components/ui-components/forms/form-input/form-input.model";
import {
  IExecuteProcedureRequest,
  IExecuteProcedureResult,
  IGetTripRequestParams,
  ResultCode,
  SharedService,
  IGetPaymentTypesResultData,
  ITripPayerFormData,
  IPaymentAttribute,
  LoginService,
  IGetGdaHeaderResponseData,
  IProcedureError,
  isProcedureError,
  isProcedureLocalizedError,
} from "@dert-services";
import { SharedApiService } from "src/app/services/api/sharedApi.service";
import { ProcedureNameEnum } from "src/app/enums/procedure-name.enum";
import { DbSchemeEnum } from "src/app/enums/db-scheme.enum";
import { VirtualProductType } from "src/app/enums/virtual-product-type.enum";
import { SobitService } from "src/app/services/sobit.service";
import { PayByCardRequest, PayByCardResult } from "src/app/models/sobit-request-data";
import { IBlobExportData, IInsertHeaderRecordParams } from "src/app/services/model/sp-procedures-params.model";
import { PaymentTypesService } from "src/app/services/payment-types.service";
import { BehaviorSubject } from "rxjs";
import { AmountWithCurrency, ICustomer, IPayment, IReservationData } from "src/app/services/model/get-trip-data.model";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { PaymentStatusCheckerService } from "src/app/services/payment-status-checker.service";
import { ReportExportService } from "src/app/services/report-export.service";
import { HeaderProcessingService } from "src/app/services/header-processing.service";

@Injectable({
  providedIn: "root",
})
export class SaleTripService {
  private readonly _spinner = inject(NgxSpinnerService);
  private readonly _router = inject(Router);
  private readonly _popUpDialogInformalService = inject(PopUpDialogInformalService);
  private readonly translate = inject(TranslateService);
  private readonly _popUpDialogConfirmService = inject(PopUpDialogConfirmService);
  private _sharedService = inject(SharedService);
  private _sharedApiService = inject(SharedApiService);
  private _loginService = inject(LoginService);
  private readonly _stateService = inject(StateService);
  private _sobitService = inject(SobitService);
  private readonly _paymentStatusCheckerService = inject(PaymentStatusCheckerService);
  private _paymentTypesService = inject(PaymentTypesService);
  private readonly _reportExportService = inject(ReportExportService);
  private readonly _headerProcessingService = inject(HeaderProcessingService);

  loadedBenefits: IGetPaymentTypesResultData[] = [];

  tripData: IReservationData = null;
  
  maxBenefitPaymentValue: number = 0;

  payments: BehaviorSubject<IPayment[]> = new BehaviorSubject<IPayment[]>([]);


  async getFormattedBenefitData(context?: PaymentContext): Promise<DropdownOption[]> {

    if (this.loadedBenefits.length === 0) {
       await this.getBenefitTypes(context);
    }

    const formattedData = this._sharedService.getFormattedBenefitData(this.loadedBenefits);
    return [...formattedData];
  }

  getBenefitType(benefit: string): TenderType | null {
    return this._sharedService.getBenefitType(benefit, this.loadedBenefits);
  }

  getBenefitNominals(benefit: string): DropdownOption[] | null {
    return this._sharedService.getBenefitNominals(benefit, this.loadedBenefits);
  }

  async getTrip(BNR: string): Promise<any> {
    this._spinner.show();
    const dataLoaded = await this.loadTripData(BNR);
    if (!dataLoaded) {
      this._spinner.hide();
      this._router.navigate(["sale"]);
    }
  }

  async searchTrip(bnr?: string, isReturn = false) {
    let _bnr: string | null = bnr;
    if (!bnr) {
      const result = await this._popUpDialogConfirmService.confirm({
        title: "webPos.Trip.SearchTrip",
        message: "webPos.Trip.EnterTripNumber",
        confirmButtonText: "webPos.Search",
        showInput: true,
        inputType: FormInputType.Text,
        inputPlaceholder: "webPos.Trip.TripNumber",
        panelClass: "search-trip-dialog"
      });

      if (result?.input) {
        _bnr = result.input;
      }
    }
    if (_bnr) {
      const tripDataLoaded = await this.loadTripData(_bnr);
      if (tripDataLoaded) {
        const navigateTo = isReturn ? "trip-return" : "trip";
        this._router.navigate([`${navigateTo}`], {
          queryParams: { bnr: _bnr },
        });
      }
      else {
        this._spinner.hide();
        // await this._popUpDialogInformalService.confirm({
        //   title: this.translate.instant("webPos.Errors.Error"),
        //   message: this.translate.instant("webPos.Trip.TripNotFound"),
        //   type: PopUpInformalType.Error,
        // });
        this._router.navigate(["sale"]);
      }
    }
  }

  clearData() {
    this.tripData = null;
  }

  async payByCard(
    paymentUID: string,
    valueToPay: number,
    isReturn: boolean,
    currencyIsoCode: string,
    bnr: string,
    showFailureDialog: boolean = true,
  ): Promise<PayByCardResult> {

    const paymentResult = await this._sobitService.payByCard({
      paymentUID,
      valueToPay,
      currencyIsoCode,
      isReturn,
      showFailureDialog,
      invNumber: isReturn ? undefined : bnr,
    } satisfies PayByCardRequest);
    if (paymentResult.outcome === "accepted") {
      await this.reloadData();
    }
    return paymentResult;
  }

  async finishPayment(headerId?: number | null, emailAddress?: string) {
    const headerIdForPrint = headerId ?? undefined;
    const dialogMessage = "webPos.SuccessfullyPayed";

    const result = await this._popUpDialogInformalService.confirm({
      title: this.translate.instant("webPos.Done"),
      message: this.translate.instant(dialogMessage),
      type: PopUpInformalType.Success,
      showPrintReceipt: true,
      showSendReceiptByEmail: true,
      emailAddress: emailAddress || '',
    });

    this._spinner.hide();

    if (result?.printReceipt) {
      await this._reportExportService.downloadPdf(
        undefined,
        [{ key: 'HeaderID', value: headerIdForPrint.toString() }],
        true
      );
    }
    
    // Send email independently if requested
    if (result?.email && headerIdForPrint) {
      await this._reportExportService.sendPdfByEmail(
        result.email,
        headerIdForPrint
      );
    }
    
    await this.reloadData();
  }


  async processPayment(
    paymentType: TenderType,
    paymentName: string,
    blobData: ITripPayerFormData,
    paymentValue: number,
    isReturn: boolean,
    paymentAttributes?: Array<IPaymentAttribute>,
    emailAddress?: string,
  ): Promise<{
    success: boolean;
    paymentUID?: string | null;
    headerUid?: string | null;
    errorMessage?: string | null;
    paymentOutcome?: PayByCardResult["outcome"];
  }> {
    console.log('[SaleTripService.processPayment] emailAddress parameter:', emailAddress);
    console.log('[SaleTripService.processPayment] blobData:', blobData);

    const valueToPay = isReturn ? -paymentValue : paymentValue;

    const formattedBlobData: IBlobExportData = {
      id: this.tripData.reservation.reservationId || null,
      paymentValue: valueToPay,
      paymentName: paymentName,
      paymentAttributes: paymentAttributes,
      saleName: null,
      customerName: blobData.ordererName,
      customerSurname: blobData.ordererSurname,
      customerStreet: blobData.street,
      customerCity: blobData.city,
      customerCountry: blobData.state,
      customerZipCode: blobData.postalCode,
      customerPhoneNumber: null,
      customerEmailAddress: null,
      customerCIN: blobData.CIN || null,
      customerTIN: blobData.TIN || null,
      description: this.tripData.reservation.destinationName || null,
      visualStyle: null,
      sendEmail: null,
      virtualProductType: isReturn ? VirtualProductType.TripReturn : VirtualProductType.Trip,
      virtualProductText: this.tripData.reservation.destinationName,
      marketCode: this.tripData.reservation.systemInfo?.reservationProperties
        ?.find(property => property.key === "MarketCode")
        ?.value?.trim() || null,
    };

    this._spinner.show();

    const virtualProductType = isReturn
      ? VirtualProductType.TripReturn
      : VirtualProductType.Trip;

    const bnr = this.tripData?.reservation.reservationId;

    const model: IInsertHeaderRecordParams = {
      DataBlob: formattedBlobData,
      VirtualProductType: virtualProductType,
      DocID: bnr,
      PaymentType: paymentType,
      PaymentName: paymentName,
      CurrencyISOCode: this._loginService.user.CurrencyCode,
      PaymentValue: valueToPay,
    };

    const createPaymentRecordRes = await this._sharedService.createPaymentRecord(model);

    if (!createPaymentRecordRes) {
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.Trip.ServerCannotConnect"),
        type: PopUpInformalType.Error,
      });
      return { success: false };
    }

    const headerUid = createPaymentRecordRes.data?.HeaderUID;

    if (!headerUid) {
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.Trip.PaymentNotCreated"),
        type: PopUpInformalType.Error,
      });
      return { success: false };
    }

    let headerProcessingResult: IExecuteProcedureResult<IGetGdaHeaderResponseData> | null = null;
    
    if (
      paymentType === TenderType.Cash ||
      paymentType === TenderType.Virtual ||
      paymentType === TenderType.Cheque ||
      paymentType === TenderType.Voucher
    ) {
      try {
        headerProcessingResult = await this._headerProcessingService.monitorHeader(headerUid);
      } catch (error) {
        console.error("Header processing failed", error);
      }

      const headerIdForPrint = this._headerProcessingService.getHeaderIdFromResult(
        headerProcessingResult,
      );

      console.log('[SaleTripService.processPayment] Calling finishPayment with emailAddress:', emailAddress);
      await this.finishPayment(headerIdForPrint, emailAddress);

      return { success: true, headerUid };

    }

    if (
      paymentType === TenderType.Card ||
      paymentType === TenderType.ElectronicVoucher
    ) {
      const paymentUID = createPaymentRecordRes.data?.PaymentUID;
      if (!paymentUID) {
        await this._popUpDialogInformalService.confirm({
          title: this.translate.instant("webPos.Errors.Error"),
          message: this.translate.instant("webPos.Trip.PaymentNotCreated"),
          type: PopUpInformalType.Error,
        });
        return { success: false, headerUid };
      }

      if (this.isOfflineCardPayment(paymentAttributes)) {
        const paymentRegistered = await this.registerOfflineCardPayment(
          paymentUID,
          headerUid,
          paymentAttributes,
          emailAddress,
        );

        return { success: paymentRegistered, paymentUID, headerUid };
      }

      const paymentResult = await this.payByCard(
        paymentUID,
        paymentValue,
        isReturn,
        model.CurrencyISOCode,
        bnr,
        false,
      );

      if (paymentResult.outcome !== "accepted") { /* Payment failed, timeout, whatever */
        return {
          success: false,
          paymentUID,
          headerUid,
          errorMessage: paymentResult.message,
          paymentOutcome: paymentResult.outcome,
        };
      }

      const cardDialogResult = await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Done"),
        message: this.translate.instant("webPos.SuccessfullyPayed"),
        type: PopUpInformalType.Success,
        showPrintReceipt: true,
        showSendReceiptByEmail: true,
        emailAddress: emailAddress || '',
      });
      console.log('[SaleTripService.processPayment] After confirm, cardDialogResult:', cardDialogResult);

      try {
        headerProcessingResult = await this._headerProcessingService.monitorHeader(headerUid);
      } catch (error) {
        console.error("Header processing failed", error);
      }

      const headerIdForPrint = this._headerProcessingService.getHeaderIdFromResult(
        headerProcessingResult,
      );

      if (cardDialogResult?.printReceipt && headerIdForPrint) {
        await this._reportExportService.downloadPdf(
          undefined,
          [{ key: 'HeaderID', value: headerIdForPrint.toString() }],
          true
        );
      }
      
      // Send email independently if requested
      if (cardDialogResult?.email && headerIdForPrint) {
        await this._reportExportService.sendPdfByEmail(
          cardDialogResult.email,
          headerIdForPrint
        );
      }

      await this.reloadData();
      return { success: true, paymentUID, headerUid };
    }

    return { success: true, headerUid };
   
  }

  async registerOfflineCardPayment(
    paymentUID: string,
    headerUid: string,
    paymentAttributes?: Array<IPaymentAttribute>,
    emailAddress?: string,
  ): Promise<boolean> {
    const paymentRegistered = await this._sobitService.registerOfflineCardPayment(paymentUID, paymentAttributes);

    if (!paymentRegistered) {
      return false;
    }

    let headerProcessingResult: IExecuteProcedureResult<IGetGdaHeaderResponseData> | null = null;

    try {
      headerProcessingResult = await this._headerProcessingService.monitorHeader(headerUid);
    } catch (error) {
      console.error("Header processing failed", error);
    }

    const headerIdForPrint = this._headerProcessingService.getHeaderIdFromResult(
      headerProcessingResult,
    );

    await this.finishPayment(headerIdForPrint, emailAddress);
    return true;
  }

  async getBenefitTypes(context?: PaymentContext): Promise<IGetPaymentTypesResultData[]> {
    this.loadedBenefits = (await this._paymentTypesService.getBenefitTypesWithNominals(context)) ?? [];
    return this.loadedBenefits;
  }

  /**
   * Loads trip data from server and set to tripData property
   * @param BNR 
   * @returns true if data loaded successfully
   */
  private async loadTripData(BNR: string): Promise<boolean | null> {
    this._spinner.show();
    const requestParams: IGetTripRequestParams = {
      ReservationId: BNR,
      VirtualProductTypes: [VirtualProductType.Trip, VirtualProductType.TripReturn],
    };

    const request: IExecuteProcedureRequest<IGetTripRequestParams> = {
      Parameters: requestParams,
      ProcedureName: ProcedureNameEnum.GetTripData,
      DbScheme: DbSchemeEnum.Dert,
    };

    // Response from DERT 
    const tripResponse: IExecuteProcedureResult<IReservationData | IProcedureError> = await this._sharedApiService.executeProcedure(request);
    this._spinner.hide();

    if (!tripResponse) {
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.Trip.ServerCannotConnect"),
        type: PopUpInformalType.Error,
      });
      return null;
    }
    if (tripResponse.result !== ResultCode.OK || tripResponse.data === null) {
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.Trip.TripNotFound"),
        type: PopUpInformalType.Error,
      });
      return null;
    }

    if (isProcedureLocalizedError(tripResponse.data)) {
      this.clearTripData();
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.getLocalizedErrorMessage(tripResponse.data.localizationCode),
        type: PopUpInformalType.Error,
      });
      return null;
    }

    if (isProcedureError(tripResponse.data)) {
      this.clearTripData();
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.Trip.TripNotFound"),
        type: PopUpInformalType.Error,
      });
      return null;
    }

    //this.loadedTripData = tripResponse.data;
    this.tripData = tripResponse.data;

    this.tripData.reservation.paymentSummary.totalPrice ??= {} as AmountWithCurrency;
    this.tripData.reservation.paymentSummary.totalPaid ??= {} as AmountWithCurrency;
    
    this.tripData.reservation.customer ??= {} as ICustomer;

    this.tripData.payments ??= [];

    if (!this.tripData) {
      return null;
    }
    this.payments.next(this.tripData.payments);
    await this.checkPayments(this.tripData.payments);

    return true;
  }

  private async checkPayments(payments: Array<IPayment>) {
    const checkResult = await this._paymentStatusCheckerService.checkAndUpdatePaymentStatusesIfPending(payments);
    
    if (checkResult) {
      this.payments.next(this.tripData.payments);
    } else {
      // If check failed, still set payments (error is already shown by service)
      this.payments.next(payments);
    }
  }

  private async reloadData() {   
    await this.getTrip(this.tripData.reservation.reservationId);
    this._spinner.hide();
  }

  private clearTripData(): void {
    this.tripData = null;
    this.payments.next([]);
  }

  private getLocalizedErrorMessage(localizationCode: string): string {
    const fallbackCode = "webPos.Errors.UnexpectedError";
    const translatedMessage = this.translate.instant(localizationCode);

    if (typeof translatedMessage !== "string" || translatedMessage === localizationCode) {
      return this.translate.instant(fallbackCode);
    }

    return translatedMessage;
  }

  private isOfflineCardPayment(paymentAttributes?: Array<IPaymentAttribute> | null): boolean {
    return !!paymentAttributes?.some(attribute =>
      attribute?.Key === "Type"
      && attribute?.Value?.toString?.().toUpperCase() === "OFFLINE"
    );
  }
}
